import { FC, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useMethods } from '@lhb/hook';
import { isArray, isNotEmptyAny } from '@lhb/func';
import { checkDynamicFormUploadStatus } from '../../ts-config';
import { footprintCreate, saveChancePoint } from '@/common/api/expandStore/chancepoint';
import { checkMatchBusinessProperty, ControlType, } from '@/common/components/business/DynamicComponent/config';
import { AlwaysRequiredChanceIndentifications } from '@/common/components/business/DynamicComponent/customize';
import { radioEnum } from '@/common/components/business/DynamicComponent/components/Footprint';
import { checkIsInCluster } from '../CheckIsInCluster';
import styles from '../index.module.less';
import { Button, Space, message } from 'antd';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import { useBigdataFn } from '@/common/utils/bigdata';

/**
 * 机会创建抽屉-底部
 * @description
 * @demo
 */
const Component:FC<{
  id,
  drawerData,
  assignedData,
  form,
  formData,
  isExpandStoreTaskIn,
  needAssiocateChance,
  supportDirectApproval,
  isFootprintValue,
  saveSucceedRef,
  tiledFormDataRef,
  setClusterHintModalOpen,
  /** 拓店任务-提交点位评估 */
  handleExpandStoreApproved,
  update,
  onSearch,
  closeDrawer,
  confirmClose,
}> = ({
  id,
  drawerData,
  assignedData,
  form,
  formData,
  isExpandStoreTaskIn,
  needAssiocateChance,
  supportDirectApproval,
  isFootprintValue,
  saveSucceedRef,
  tiledFormDataRef,
  setClusterHintModalOpen,
  handleExpandStoreApproved,
  update,
  onSearch,
  closeDrawer,
  confirmClose,
}) => {

  /* state */
  const tenantCheckData = useSelector((state: any) => state.common.tenantCheck);
  /** 是否益禾堂租户 */
  const isYiHeTang = useMemo(() => {
    return !!tenantCheckData?.isYiHeTang;
  }, [tenantCheckData]);
  const bigdataFn = useBigdataFn(); // 使用埋点 Hook

  const dynamicOtherFormNameArr = useSelector((state: any) => state.common.dynamicOtherFormNameArr); // 存放“其他”表单的formName
  const lockRef = useRef(false);

  /* hooks */

  /* methods */
  const methods = useMethods({

    /**
     * 表单提交，目前的写法只能校验当前表单页的必填项，因为渲染的时候只渲染对应的tab下的表单项
     * @param isApproveForStore 拓店任务-点位评估场景下，点击提交时传true，后续需要进行点位评估
     */
    confirmHandle(isApproveForStore = false) {
      // 已保存成功，不再处理
      if (saveSucceedRef.current) {
        return;
      }
      // 无论任何情况下，机会点表单必须检查必填的特殊字段
      let checkFields: any[] = [];
      const needCheck = needAssiocateChance || isApproveForStore;
      if (!needCheck && isArray(tiledFormDataRef.current)) {
        const allIndentifications = tiledFormDataRef.current.map(itm => itm.identification);
        checkFields = AlwaysRequiredChanceIndentifications.filter((itm: any) => allIndentifications.includes(itm));
        // 上传类型的组件也要校验是否上传完毕，如果还在上传中也要校验
        const uploadComs = tiledFormDataRef.current?.filter((itm: any) => itm.controlType === ControlType.UPLOAD.value);
        if (!checkDynamicFormUploadStatus(uploadComs)) return;
      }

      // 创建机会点后自动关联任务场景下，需要检查匹配商圈组件值（目前只有益禾堂租户会检查其中的集客点是否已选择）
      if (needCheck && !checkMatchBusinessProperty(isYiHeTang, tiledFormDataRef.current)) {
        V2Message.warning('请先选择商圈内集客点'); // 产品定的文案
        return;
      }

      // 全部字段检查时传undefined即无入参
      form
        .validateFields(needCheck ? undefined : [...checkFields, ...dynamicOtherFormNameArr])
        .then(async () => {
          // 拿到踩点组件的值，校验信息是否填写完全（对目前的写法只能校验当前表单页的必填项的校验补充）
          const footprintItem:any = tiledFormDataRef.current.filter((item) => {
            return item.identification === 'locationFootprint';
          });
          let footprintValue;
          if (footprintItem.length !== 0 && isArray(footprintItem)) {
            footprintValue = JSON.parse(footprintItem[0]?.textValue || null);
          }
          // 与产品大鹏确认，对于审批流程配置和可见可写不为空配置中，在supportDirectApproval===1情况下（支持审批流程配置)仅当access===2（可写）的状态下需要校验踩点组件是否不为空；supportDirectApproval===2情况下，表单都可见，所以也需要判断是否填写踩点组件
          let access;
          if (footprintItem.length !== 0 && isArray(footprintItem)) {
            access = JSON.parse(footprintItem[0]?.access || null);
          }
          // footprintItem不为空数组，选了是没填消息，或者没选
          if ((isArray(footprintItem) && footprintItem.length) &&
          ((supportDirectApproval === 1 && access === 2) || supportDirectApproval === 2) &&
          (footprintValue?.dynamicComponent_footprint_isStart === radioEnum.AGREE || !isNotEmptyAny(footprintValue))) {
            // 判断踩点组件信息是否填写完全
            if (!isNotEmptyAny(footprintValue?.dynamicComponent_footprint_checkRule) ||
              !isNotEmptyAny(footprintValue?.dynamicComponent_footprint_checkPeriod)) {
              message.error('踩点信息填写不完全');
              return;
            }
          }

          if (lockRef.current) return;
          lockRef.current = true;

          const propertyValues = tiledFormDataRef.current.map((item: any) => ({
            id: item.id,
            propertyId: item.propertyId,
            identification: item.identification,
            textValue: item.textValue,
          }));
          // if (process.env.NODE_ENV === 'development' && propertyValues) {
          //   console.log(`表单submit的值`, propertyValues);
          //   return;
          // }
          const params: any = {
            // type: formData.type,
            customerTypeId: formData.customerTypeId,
            dynamicRelationId: formData.dynamicRelationId,
            propertyValues,
          };
          if (id) {
            params.id = id;
          }
          // 按照表单数据判断是否机会点是否在所属商圈中
          if (!checkIsInCluster(formData, propertyValues)) {
            setClusterHintModalOpen(true);
            lockRef.current = false;
            return;
          }
          const data = await saveChancePoint(params).finally(() => {
            lockRef.current = false;
          });
          saveSucceedRef.current = true;

          // 拿到机会点的 id=>data，需要创建踩点任务 且不是在编辑机会点情况下 再创建踩点任务
          if (footprintValue?.dynamicComponent_footprint_isStart === radioEnum.AGREE && !isFootprintValue) {
            if (!data) {
              message.error('机会点尚未保存');
              return;
            }
            await footprintCreate({
              chancePointId: data,
              propertyValues,
              checkRule: footprintValue.dynamicComponent_footprint_checkRule,
              checkPeriod: [{
                start: footprintValue.dynamicComponent_footprint_checkPeriod[0],
                end: footprintValue.dynamicComponent_footprint_checkPeriod[1],
              }]
            });

          }

          if (!isExpandStoreTaskIn) {
            form.resetFields(); // 清空表单
          }
          // 编辑时更新机会点详情
          if (id) {
            update && update();
          }
          if (onSearch) {
            // 客流计数创建成功后回传机会点id
            // 拓店任务需要自动关联机会场景创建成功后回传机会点id
            (isArray(assignedData) && assignedData.length || needAssiocateChance) ? onSearch(data) : onSearch({});
          }
          // onSearch && onSearch({}); // 刷新列表
          bigdataFn('d01dc884-b271-4767-a623-e4d669ab8b18', '创建机会点详情-确定', '点击了创建机会点详情-确定', { op_block: '创建机会点', op_event: ``, });

          // 拓店--点位评估
          if (isApproveForStore && isExpandStoreTaskIn) {
            handleExpandStoreApproved?.(drawerData.expandStoreData, drawerData);
            return;
          }

          confirmClose(); // 关闭抽屉
        })
        .catch((errorInfo: any) => {
          console.log(`errorInfo1`, errorInfo);
          if (isArray(errorInfo?.errorFields) && errorInfo?.errorFields.length && isArray(errorInfo?.errorFields[0].errors) && errorInfo?.errorFields[0].errors.length) {
            V2Message.warning(errorInfo?.errorFields[0].errors[0]);
          }
        });
    },

  });

  return (<Space size={12} className={styles.footerCon}>
    <Button onClick={closeDrawer}>取消</Button>
    <Button type='primary' onClick={() => methods.confirmHandle()}>{isExpandStoreTaskIn ? '保存' : '确认'}</Button>
    {isExpandStoreTaskIn ? <Button type='primary' onClick={() => methods.confirmHandle(true)}>提交</Button> : <></>}
  </Space>);
};

export default Component;
